import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Alert,
  AlertTitle,
  CircularProgress,
  Stack,
  Chip,
} from '@mui/material';
import { teamAdminApi, type BillingMismatchResult } from '../../api/team_admin_api';

export const ManagerTeamBilling = observer(() => {
  const { teamKey } = useParams<{ teamKey: string }>();

  const [result, setResult] = useState<BillingMismatchResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!teamKey) return;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        setResult(await teamAdminApi.billingMismatch(teamKey));
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to run billing check');
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [teamKey]);

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Billing Mismatch Check
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Compares live Recurly subscription seat counts to the number of active
          invites on each plan. A mismatch means the billing seat count has drifted
          from the team's records.
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : result ? (
          <Stack spacing={2}>
            {result.error && (
              <Alert severity="error">
                <AlertTitle>Could not reach Recurly</AlertTitle>
                {result.error}
              </Alert>
            )}

            {result.note && <Alert severity="info">{result.note}</Alert>}

            {result.recurly_account && (
              <Typography variant="body2" color="text.secondary">
                Recurly account: <code>{result.recurly_account}</code>
              </Typography>
            )}

            {result.mismatches.map((m) => (
              <Alert severity="warning" key={m.plan_code}>
                <AlertTitle>Mismatch on plan {m.plan_code}</AlertTitle>
                Recurly seat quantity (<strong>{m.recurly_quantity}</strong>) ≠ active
                invites (<strong>{m.local_count}</strong>).
                {m.sub_uuids.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Subscriptions: {m.sub_uuids.join(', ')}
                    </Typography>
                  </Box>
                )}
              </Alert>
            ))}

            {result.ok.map((m) => (
              <Box
                key={m.plan_code}
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <Chip label="OK" size="small" color="success" />
                <Typography variant="body2">
                  {m.plan_code}: {m.recurly_quantity} seats = {m.local_count} invites
                </Typography>
              </Box>
            ))}

            {!result.error &&
              !result.note &&
              result.mismatches.length === 0 &&
              result.ok.length === 0 && (
                <Alert severity="info">No active plans to compare.</Alert>
              )}

            {!result.error && result.mismatches.length === 0 && result.ok.length > 0 && (
              <Alert severity="success">All plans are aligned with Recurly.</Alert>
            )}
          </Stack>
        ) : null}
      </CardContent>
    </Card>
  );
});
